// Library imports
import React, { useEffect, useRef } from 'react';
import {
    Scene,
    PerspectiveCamera,
    PlaneGeometry,
    MeshStandardMaterial,
    TextureLoader,
    Mesh,
    AmbientLight,
    DirectionalLight,
    WebGLRenderer,
    Vector3,
    Spherical,
} from 'three';

// Local imports
import { Measurement, VizSurface } from '../../../../../../typings/reality-capture/volumes/models';
import './volume-threejs.css';

// ---------------------------------------------------------------------------
// ThirdPersonControls
// ---------------------------------------------------------------------------
// Replaces OrbitControls with a control scheme optimised for large terrain:
//
//   • Left-drag  – orbit (rotate camera around the look-at target)
//   • Right-drag – pan   (translate the look-at target in the camera's
//                         horizontal plane, like grabbing the terrain)
//   • Scroll     – zoom  (scale the orbit radius)
//   • WASD / ↑↓←→ – walk (same translation but via keyboard; speed scales
//                          with zoom distance for a consistent feel)
//
// The camera always remains above the terrain (phi clamped to < 90 °).
// ---------------------------------------------------------------------------
class ThirdPersonControls {
    camera: PerspectiveCamera;
    domElement: HTMLElement;
    /** World-space point the camera orbits around. */
    target: Vector3;
    /** Spherical coords: radius (distance), phi (↕ angle from Y+), theta (↔). */
    spherical: Spherical;

    private isOrbitDown = false;
    private isPanDown   = false;
    private lastX = 0;
    private lastY = 0;
    private keysDown: Set<string> = new Set();

    constructor(camera: PerspectiveCamera, domElement: HTMLElement) {
        this.camera     = camera;
        this.domElement = domElement;
        this.target     = new Vector3();
        // Initial position: 300 units away, 45 ° above horizon.
        this.spherical  = new Spherical(300, Math.PI / 4, 0);

        // Let the canvas element receive keyboard events after a click.
        domElement.setAttribute('tabindex', '0');
        domElement.style.outline = 'none';

        domElement.addEventListener('pointerdown',  this._onPointerDown);
        domElement.addEventListener('pointermove',  this._onPointerMove);
        domElement.addEventListener('pointerup',    this._onPointerUp);
        domElement.addEventListener('wheel',        this._onWheel, { passive: false });
        domElement.addEventListener('keydown',      this._onKeyDown);
        domElement.addEventListener('keyup',        this._onKeyUp);
        domElement.addEventListener('contextmenu',  this._prevent);
    }

    /**
     * Call once per frame with the elapsed time in seconds.
     * Updates camera position from the current spherical coords + key state.
     */
    update(delta: number): void {
        const sinT = Math.sin(this.spherical.theta);
        const cosT = Math.cos(this.spherical.theta);

        // Horizontal forward / right vectors aligned with the camera's yaw —
        // Y component is zero so movement stays flat on the terrain.
        const forward = new Vector3(-sinT, 0, -cosT);
        const right   = new Vector3( cosT, 0, -sinT);

        // Walk speed proportional to orbit radius: intuitive at any zoom level.
        const walkSpeed = this.spherical.radius * 0.8 * delta;

        if (this.keysDown.has('w') || this.keysDown.has('W') || this.keysDown.has('ArrowUp'))
            this.target.addScaledVector(forward,  walkSpeed);
        if (this.keysDown.has('s') || this.keysDown.has('S') || this.keysDown.has('ArrowDown'))
            this.target.addScaledVector(forward, -walkSpeed);
        if (this.keysDown.has('d') || this.keysDown.has('D') || this.keysDown.has('ArrowRight'))
            this.target.addScaledVector(right,    walkSpeed);
        if (this.keysDown.has('a') || this.keysDown.has('A') || this.keysDown.has('ArrowLeft'))
            this.target.addScaledVector(right,   -walkSpeed);

        // Convert spherical → Cartesian and place camera.
        const sinPhi = Math.sin(this.spherical.phi);
        const cosPhi = Math.cos(this.spherical.phi);
        this.camera.position.set(
            this.target.x + this.spherical.radius * sinPhi * sinT,
            this.target.y + this.spherical.radius * cosPhi,
            this.target.z + this.spherical.radius * sinPhi * cosT,
        );
        this.camera.lookAt(this.target);
    }

    private _onPointerDown = (e: PointerEvent): void => {
        // Left button → orbit; right button → pan.
        if (e.button === 0) this.isOrbitDown = true;
        if (e.button === 2) this.isPanDown   = true;
        this.lastX = e.clientX;
        this.lastY = e.clientY;
        this.domElement.setPointerCapture(e.pointerId);
        this.domElement.focus();
    };

    private _onPointerMove = (e: PointerEvent): void => {
        const dx = e.clientX - this.lastX;
        const dy = e.clientY - this.lastY;
        this.lastX = e.clientX;
        this.lastY = e.clientY;

        if (this.isOrbitDown) {
            // Rotate horizontally (theta) and vertically (phi); clamp phi so
            // the camera never dips below the terrain or flips over the top.
            this.spherical.theta -= dx * 0.005;
            this.spherical.phi    = Math.max(
                0.05,
                Math.min(Math.PI * 0.48, this.spherical.phi - dy * 0.005),
            );
        }

        if (this.isPanDown) {
            // Grab-and-drag: terrain follows the pointer.
            const sinT = Math.sin(this.spherical.theta);
            const cosT = Math.cos(this.spherical.theta);
            const panSpeed = this.spherical.radius * 0.001;
            // right vector: drag right → terrain moves right
            this.target.addScaledVector(new Vector3(-cosT, 0, sinT),   dx * panSpeed);
            // forward vector: invert vertical drag direction to match orbiting
            this.target.addScaledVector(new Vector3(-sinT, 0, -cosT), dy * panSpeed);
        }
    };

    private _onPointerUp = (e: PointerEvent): void => {
        this.isOrbitDown = false;
        this.isPanDown   = false;
        try { this.domElement.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    };

    private _onWheel = (e: WheelEvent): void => {
        e.preventDefault();
        const factor = e.deltaY > 0 ? 1.12 : 1 / 1.12;
        this.spherical.radius = Math.max(5, Math.min(5000, this.spherical.radius * factor));
    };

    private _onKeyDown = (e: KeyboardEvent): void => {
        this.keysDown.add(e.key);
        // Prevent arrow keys from scrolling the page while the canvas is focused.
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(e.key))
            e.preventDefault();
    };

    private _onKeyUp = (e: KeyboardEvent): void => {
        this.keysDown.delete(e.key);
    };

    private _prevent = (e: Event): void => { e.preventDefault(); };

    dispose(): void {
        this.domElement.removeEventListener('pointerdown',  this._onPointerDown);
        this.domElement.removeEventListener('pointermove',  this._onPointerMove);
        this.domElement.removeEventListener('pointerup',    this._onPointerUp);
        this.domElement.removeEventListener('wheel',        this._onWheel);
        this.domElement.removeEventListener('keydown',      this._onKeyDown);
        this.domElement.removeEventListener('keyup',        this._onKeyUp);
        this.domElement.removeEventListener('contextmenu',  this._prevent);
    }
}

// ---------------------------------------------------------------------------
// createTerrainMesh (module-level helper — no closure dependencies)
// ---------------------------------------------------------------------------
// The displacement texture encodes terrain height normalized to the full 0–255
// byte range over [bounds.z_min, bounds.z_max] (see reality-capture's
// normalize_dem_to_byte). three.js reads the texture as [0,1], so scaling by
// the bounds' height span reconstructs metres (× yScale); no bias is needed
// because pixel 0 is the terrain base. This keeps sites above 255 m altitude
// from clamping.
type TerrainMesh = { mesh: Mesh; heightSpan: number };

async function createTerrainMesh(terrain: VizSurface, yScale: number): Promise<TerrainMesh> {
    const { x_min, x_max, y_min, y_max, z_min, z_max } = terrain.bounds;
    const width  = x_max - x_min;
    const depth  = y_max - y_min;
    const heightSpan = (z_max - z_min) * yScale;

    const geometry = new PlaneGeometry(width, depth, 100, 100);
    geometry.rotateX(-Math.PI / 2);
    geometry.center();

    const loader = new TextureLoader();
    const material = new MeshStandardMaterial({
        map:               loader.load(terrain.heatmap_url),
        displacementMap:   loader.load(terrain.url),
        alphaMap:          loader.load(terrain.alpha_url),
        displacementScale: heightSpan,
        displacementBias:  0,
        transparent: true,
        depthTest:   true,
        alphaTest:   0.5,
    });

    const mesh = new Mesh(geometry, material);
    mesh.position.set(0, 0, 0);
    // The geometry is already laid flat on the ground plane above. Rotating it
    // again around Z introduces the incorrect 90-degree scene offset.
    return { mesh, heightSpan };
}

// ---------------------------------------------------------------------------
// VolumeThreejs component
// ---------------------------------------------------------------------------
export const VolumeThreejs = ({ measurement, scale }: { measurement: Measurement; scale: number }) => {
    const mountRef     = useRef<HTMLDivElement>(null);
    const sceneRef     = useRef(new Scene());
    const cameraRef    = useRef(new PerspectiveCamera(60, 1, 0.1, 10000));
    const rendererRef  = useRef<WebGLRenderer | null>(null);
    const controlsRef  = useRef<ThirdPersonControls | null>(null);
    const meshRef      = useRef<Mesh | null>(null);
    const animFrameRef = useRef<number | null>(null);
    const lastTimeRef  = useRef(0);

    // ── One-time setup: renderer, lighting, controls, animation loop ─────────
    useEffect(() => {
        const el     = mountRef.current!;
        const camera = cameraRef.current;
        const scene  = sceneRef.current;

        camera.aspect = el.clientWidth / el.clientHeight;
        camera.updateProjectionMatrix();

        scene.add(new AmbientLight(0xffffff, 0.7));
        const dirLight = new DirectionalLight(0xffffff, 1.0);
        dirLight.position.set(500, 2000, 2000).normalize();
        scene.add(dirLight);

        const renderer = new WebGLRenderer({ alpha: true });
        renderer.setSize(el.clientWidth, el.clientHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setClearColor(0xF1F5F9);
        el.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        const controls = new ThirdPersonControls(camera, el);
        controlsRef.current = controls;

        const animate = (time: number): void => {
            animFrameRef.current = requestAnimationFrame(animate);
            const delta = Math.min((time - lastTimeRef.current) / 1000, 0.1);
            lastTimeRef.current = time;
            controls.update(delta);
            renderer.render(scene, camera);
        };
        animFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animFrameRef.current !== null) cancelAnimationFrame(animFrameRef.current);
            controls.dispose();
            renderer.dispose();
            if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
        };
    }, []);

    // ── Replace mesh whenever measurement or vertical scale changes ───────────
    useEffect(() => {
        const scene = sceneRef.current;
        if (meshRef.current) {
            scene.remove(meshRef.current);
            meshRef.current = null;
        }
        if (!measurement?.viz_surface) return;

        createTerrainMesh(measurement.viz_surface, scale).then(({ mesh: newMesh, heightSpan }) => {
            meshRef.current = newMesh;
            scene.add(newMesh);

            // Fit the initial camera distance to the terrain footprint.
            const { x_min, x_max, y_min, y_max } = measurement.viz_surface.bounds;
            const extent = Math.max(x_max - x_min, y_max - y_min);
            if (controlsRef.current) {
                // Orbit around the vertical middle of the displaced terrain.
                controlsRef.current.target.set(0, heightSpan / 2, 0);
                controlsRef.current.spherical.radius = extent * 1.2;
                controlsRef.current.spherical.phi    = Math.PI / 4;
                controlsRef.current.spherical.theta  = 0;
            }
        });
    }, [measurement, scale]);

    // ── Resize observer ───────────────────────────────────────────────────────
    useEffect(() => {
        const el       = mountRef.current!;
        const observer = new ResizeObserver(entries => {
            const { width, height } = entries[0].contentRect;
            rendererRef.current?.setSize(width, height);
            const cam = cameraRef.current;
            cam.aspect = width / height;
            cam.updateProjectionMatrix();
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div className="threejs-container">
            <div ref={mountRef} className="threejs-canvas" />
        </div>
    );
};
